import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import { ApiContext } from "../contexts/ApiContext";
import { useNotification } from "../contexts/NotificationContext";
import AccountService from "../service/account.service";
import UploadService from "../service/upload.service";
import UserService from "../service/user.service";
import { getCardapioUrl } from "../utils/cardapioUrl";
import { formatPhone, maskPhoneInput } from "../utils/phone";
import {
  buildUpgradeWhatsappUrl,
  formatPlanPrice,
} from "../utils/planWhatsapp";
import { BusinessHoursForm } from "./Settings/BusinessHoursSettings";

const MODULES = [
  {
    key: "tablesEnabled",
    planKey: "tables",
    icon: "icon-app",
    title: "Mesas",
    description: "Mesas com QR Code e pedidos vinculados à mesa.",
  },
  {
    key: "inventoryEnabled",
    planKey: "inventory",
    icon: "icon-basket-simple",
    title: "Estoque",
    description: "Insumos, fichas técnicas e baixa automática por pedido.",
  },
  {
    key: "printingEnabled",
    planKey: "printing",
    icon: "icon-paper",
    title: "Impressão",
    description: "Impressão dos pedidos e alerta de impressora desconectada.",
  },
];

function UserProfile() {
  const { account, user, getUser, setAccount } = useContext(ApiContext);
  const { notify } = useNotification();
  const maxUsers = account?.features?.userLimit || (account?.features?.extraUsers ? 15 : 5);

  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#16a34a");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [instagram, setInstagram] = useState("");
  const [description, setDescription] = useState("");
  const [savingModule, setSavingModule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const [users, setUsers] = useState([]);
  const [uName, setUName] = useState("");
  const [uEmail, setUEmail] = useState("");
  const [uPassword, setUPassword] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [hoursModalOpen, setHoursModalOpen] = useState(false);

  const teamCount = users.filter((u) => u.role === "USER").length;
  const cardapioUrl = getCardapioUrl(account?.menuCode);

  const loadUsers = async () => {
    try {
      const data = await UserService.list(account?.id);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const resetUserForm = () => {
    setEditingId(null);
    setUName("");
    setUEmail("");
    setUPassword("");
  };

  const handleEditUser = (u) => {
    setEditingId(u.id);
    setUName(u.name || "");
    setUEmail(u.email || "");
    setUPassword("");
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!uName.trim() || !uEmail.trim()) return;
    if (!editingId && teamCount >= maxUsers) {
      notify("tr", "danger", `Limite de ${maxUsers} usuários atingido`, "icon-alert-circle-exc");
      return;
    }
    setSavingUser(true);
    try {
      if (editingId) {
        const payload = { name: uName.trim(), email: uEmail.trim() };
        if (uPassword) payload.password = uPassword;
        await UserService.update(editingId, payload);
        notify("tr", "success", "Usuário atualizado", "icon-check-2");
      } else {
        await UserService.create({ name: uName.trim(), email: uEmail.trim(), password: uPassword, accountId: account.id });
        notify("tr", "success", "Usuário criado", "icon-check-2");
      }
      resetUserForm();
      await loadUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao salvar usuário";
      notify("tr", "danger", Array.isArray(msg) ? msg.join(", ") : msg, "icon-alert-circle-exc");
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Remover este usuário?")) return;
    try {
      await UserService.remove(id);
      notify("tr", "success", "Usuário removido", "icon-check-2");
      if (editingId === id) resetUserForm();
      await loadUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || "Erro ao remover usuário";
      notify("tr", "danger", Array.isArray(msg) ? msg.join(", ") : msg, "icon-alert-circle-exc");
    }
  };

  useEffect(() => {
    if (account) {
      setLogoUrl(account.logoUrl || "");
      setPrimaryColor(account.primaryColor || "#16a34a");
      setPhone(formatPhone(account.phone));
      setWhatsapp(formatPhone(account.whatsapp));
      setInstagram(account.instagram ? `@${account.instagram}` : "");
      setDescription(account.description || "");
    }
  }, [account]);

  const toggleModule = async (mod) => {
    if (!account?.id || savingModule) return;
    const next = !account[mod.key];
    setSavingModule(mod.key);
    try {
      await AccountService.update(account.id, { [mod.key]: next });
      setAccount((prev) => (prev && prev.id === account.id ? { ...prev, [mod.key]: next } : prev));
      notify("tr", "success", `Módulo de ${mod.title.toLowerCase()} ${next ? "ativado" : "desativado"}`, "icon-check-2");
    } catch (err) {
      const msg = err?.response?.data?.message || `Erro ao atualizar ${mod.title.toLowerCase()}`;
      notify("tr", "danger", Array.isArray(msg) ? msg.join(", ") : msg, "icon-alert-circle-exc");
    } finally {
      setSavingModule(null);
    }
  };

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  const handleLogoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      notify("tr", "warning", "Selecione um arquivo de imagem", "icon-alert-circle-exc");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const res = await UploadService.uploadSingle(file);
      setLogoUrl(res.url);
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao enviar logo", "icon-alert-circle-exc");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!account?.id) return;
    setSaving(true);
    try {
      await AccountService.update(account.id, {
        logoUrl,
        primaryColor,
        phone,
        whatsapp,
        instagram,
        description,
      });
      notify("tr", "success", "Conta atualizada com sucesso!", "icon-check-2");
      if (user?.id) await getUser(user.id);
    } catch (err) {
      console.error(err);
      notify("tr", "danger", "Erro ao atualizar conta", "icon-alert-circle-exc");
    } finally {
      setSaving(false);
    }
  };

  const plan = account?.plan;
  const aiUsage = account?.aiChatUsage;
  const aiUsagePct =
    aiUsage?.limit > 0 ? Math.min(100, Math.round((aiUsage.used / aiUsage.limit) * 100)) : 0;
  const aiLimitReached = aiUsage?.enabled && aiUsage.used >= aiUsage.limit;
  const upgradeUrl = buildUpgradeWhatsappUrl({
    accountName: account?.name,
    planName: plan?.name,
    whatsappUpgradeUrl: aiUsage?.whatsappUpgradeUrl,
  });

  return (
    <>
      <div className="content">
        <Row>
          {plan && (
            <Col md="12">
              <Card>
                <CardHeader>
                  <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 12 }}>
                    <h5 className="title mb-0">Meu plano</h5>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 12px",
                        borderRadius: 999,
                        background: plan.badgeColor || "#3b82f6",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {plan.name}
                      {plan.highlight ? " ★" : ""}
                    </span>
                  </div>
                </CardHeader>
                <CardBody>
                  <p className="mb-2" style={{ fontSize: 22, fontWeight: 700 }}>
                    {formatPlanPrice(plan.priceCents, plan.currency)}
                    <span className="text-muted" style={{ fontSize: 14, fontWeight: 500 }}>
                      {" "}/mês
                    </span>
                  </p>
                  {plan.description && (
                    <p className="text-muted mb-3">{plan.description}</p>
                  )}
                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                    {(plan.features || []).map((f) => (
                      <span key={f.key} className="plan-chip">
                        {f.label}
                        {f.limitValue != null ? ` (${f.limitValue})` : ""}
                      </span>
                    ))}
                  </div>

                  {aiUsage?.enabled && (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong style={{ fontSize: 14 }}>IA avançada com chat</strong>
                        <span className="text-muted" style={{ fontSize: 13 }}>
                          {aiUsage.used}/{aiUsage.limit} requests este mês
                        </span>
                      </div>
                      <div
                        style={{
                          height: 10,
                          borderRadius: 999,
                          background: "#e2e8f0",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${aiUsagePct}%`,
                            height: "100%",
                            background: aiLimitReached ? "#ef4444" : "#3b82f6",
                            transition: "width 0.25s ease",
                          }}
                        />
                      </div>
                      <small className="text-muted d-block mt-1">
                        O limite reseta todo dia 1.
                      </small>
                      {aiLimitReached && (
                        <div className="mt-3">
                          <p className="mb-2" style={{ color: "#b91c1c", fontSize: 14 }}>
                            Você atingiu o limite mensal do chat com IA.
                          </p>
                          <Button
                            color="success"
                            tag="a"
                            href={upgradeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Falar no WhatsApp para upgrade
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          )}

          <Col md="12">
            <Card>
              <CardHeader>
                <h5 className="title mb-1">Módulos</h5>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Ative só o que a sua operação usa. A mudança vale na hora para toda a equipe, incluindo menus e notificações.
                </p>
              </CardHeader>
              <CardBody>
                <Row>
                  {MODULES.map((mod) => {
                    const enabled = !!account?.[mod.key];
                    const available = account?.features?.modules?.[mod.planKey] !== false;
                    const busy = savingModule === mod.key;
                    const locked = !isAdmin || (!available && !enabled);
                    return (
                      <Col md="4" key={mod.key} className="mb-3">
                        <div className={`module-tile ${enabled ? "module-tile--on" : ""}`}>
                          <div className="module-tile__icon">
                            <i className={`tim-icons ${mod.icon}`} />
                          </div>
                          <div className="module-tile__body">
                            <div className="module-tile__title">
                              {mod.title}
                              {!available && <span className="module-tile__badge">Fora do plano</span>}
                            </div>
                            <small className="module-tile__desc">{mod.description}</small>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={enabled}
                            aria-label={`${enabled ? "Desativar" : "Ativar"} ${mod.title}`}
                            title={
                              !isAdmin
                                ? "Apenas administradores podem alterar"
                                : !available && !enabled
                                  ? "Disponível em outro plano"
                                  : undefined
                            }
                            className={`module-switch ${enabled ? "module-switch--on" : ""} ${busy ? "module-switch--busy" : ""}`}
                            disabled={locked || busy}
                            onClick={() => toggleModule(mod)}
                          >
                            <span className="module-switch__knob" />
                          </button>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col md="12">
            <Card>
              <CardHeader>
                <h5 className="title">Editar Conta</h5>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <p className="text-muted text-uppercase mb-3" style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
                    Cardápio digital
                  </p>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <label>Código do cardápio</label>
                        <Input value={account?.menuCode || ""} disabled type="text" />
                        {cardapioUrl && (
                          <div className="mt-2 d-flex flex-wrap" style={{ gap: 8 }}>
                            <Button
                              color="primary"
                              size="sm"
                              tag="a"
                              href={cardapioUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="tim-icons icon-world mr-1" />
                              Abrir cardápio
                            </Button>
                            <Button
                              color="default"
                              size="sm"
                              outline
                              onClick={() => {
                                navigator.clipboard.writeText(cardapioUrl);
                                notify("tr", "success", "Link copiado", "icon-check-2");
                              }}
                            >
                              <i className="tim-icons icon-single-copy-04 mr-1" />
                              Copiar link
                            </Button>
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label>Cor primária</label>
                        <div className="d-flex align-items-center" style={{ gap: 10 }}>
                          <Input
                            type="color"
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            style={{ width: 48, height: 38, padding: 2, flexShrink: 0 }}
                          />
                          <Input
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            placeholder="#16a34a"
                            type="text"
                          />
                        </div>
                        <small className="text-muted">Botões e abas do cardápio digital</small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <label>Logo</label>
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 16 }}>
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt="logo"
                              style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid #e3e3e3" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 72,
                                height: 72,
                                borderRadius: 8,
                                background: "#2b3553",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <i className="tim-icons icon-image-02" style={{ color: "#fff" }} />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 200, maxWidth: 360 }}>
                            <Input type="file" accept="image/*" onChange={handleLogoChange} disabled={uploading} />
                            {uploading && <small className="text-muted d-block mt-1">Enviando...</small>}
                            <small className="text-muted d-block mt-1">Imagem exibida no cardápio digital</small>
                          </div>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr />
                  <p className="text-muted text-uppercase mb-1" style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
                    Contato e redes sociais
                  </p>
                  <small className="text-muted d-block mb-3">
                    Aparecem no topo e no perfil da loja do cardápio digital. Deixe em branco para ocultar.
                  </small>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <label>Telefone</label>
                        <Input
                          value={phone}
                          onChange={(e) => setPhone(maskPhoneInput(e.target.value))}
                          placeholder="(31) 3333-4444"
                          inputMode="tel"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <label>WhatsApp</label>
                        <Input
                          value={whatsapp}
                          onChange={(e) => setWhatsapp(maskPhoneInput(e.target.value))}
                          placeholder="(31) 99999-8888"
                          inputMode="tel"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <label>Instagram</label>
                        <Input
                          value={instagram}
                          onChange={(e) => setInstagram(e.target.value)}
                          placeholder="@seurestaurante"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <label>Sobre a loja</label>
                        <Input
                          type="textarea"
                          value={description}
                          onChange={(e) => setDescription(e.target.value.slice(0, 300))}
                          placeholder="Ex.: Hambúrguer artesanal desde 2015. Carne 100% bovina e pão de fermentação natural."
                          rows={3}
                        />
                        <small className="text-muted">{description.length}/300 · Exibido no perfil da loja</small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr />
                  <p className="text-muted text-uppercase mb-3" style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
                    Operação da loja
                  </p>
                  <Row>
                    <Col md="6">
                      <div className="settings-tile">
                        <div className="settings-tile__title">Horário de atendimento</div>
                        <small className="text-muted d-block mb-3">
                          Dias e horários em que o cardápio aceita pedidos.
                        </small>
                        <Button color="primary" size="sm" outline onClick={() => setHoursModalOpen(true)}>
                          <i className="tim-icons icon-time-alarm mr-1" />
                          Configurar horário
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
              <CardFooter>
                <Button className="btn-fill" color="primary" onClick={handleSubmit} disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </Button>
              </CardFooter>
            </Card>
          </Col>
        </Row>

        {isAdmin && (
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <h5 className="title">Usuários ({teamCount}/{maxUsers})</h5>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    Gerencie até {maxUsers} usuários para sua equipe.
                  </p>
                </CardHeader>
                <CardBody>
                  <Form onSubmit={handleUserSubmit}>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <label>Nome</label>
                          <Input value={uName} onChange={(e) => setUName(e.target.value)} type="text" />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <label>E-mail</label>
                          <Input value={uEmail} onChange={(e) => setUEmail(e.target.value)} type="email" />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <label>{editingId ? "Nova senha (opcional)" : "Senha"}</label>
                          <Input value={uPassword} onChange={(e) => setUPassword(e.target.value)} type="password" />
                        </FormGroup>
                      </Col>
                      <Col md="3" className="d-flex align-items-end">
                        <FormGroup style={{ width: "100%" }}>
                          <Button className="btn-fill" color="primary" type="submit" disabled={savingUser} style={{ width: "100%" }}>
                            {savingUser ? "Salvando..." : editingId ? "Atualizar" : "Adicionar"}
                          </Button>
                          {editingId && (
                            <Button color="secondary" type="button" onClick={resetUserForm} style={{ width: "100%", marginTop: 8 }}>
                              Cancelar
                            </Button>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>

                  <table className="table" style={{ marginTop: 12 }}>
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Perfil</th>
                        <th className="text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id}>
                          <td>{u.name}</td>
                          <td>{u.email}</td>
                          <td>{u.role === "ADMIN" ? "Administrador" : "Usuário"}</td>
                          <td className="text-right">
                            {u.role !== "ADMIN" && (
                              <>
                                <Button color="info" size="sm" className="mr-2" onClick={() => handleEditUser(u)}>
                                  <i className="tim-icons icon-pencil" />
                                </Button>
                                <Button color="danger" size="sm" onClick={() => handleDeleteUser(u.id)}>
                                  <i className="tim-icons icon-simple-remove" />
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan="4" className="text-muted">Nenhum usuário cadastrado.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>

      <Modal
        isOpen={hoursModalOpen}
        toggle={() => setHoursModalOpen(false)}
        size="lg"
        modalClassName="modal-form business-hours-modal"
      >
        <ModalHeader toggle={() => setHoursModalOpen(false)}>Horário de atendimento</ModalHeader>
        <ModalBody>
          <BusinessHoursForm onSaved={() => setHoursModalOpen(false)} />
        </ModalBody>
      </Modal>
    </>
  );
}

export default UserProfile;
